import React from "react";
import Chart from "react-apexcharts";
import Card from "components/card";
import { useBodyDarkClass } from "hooks/useBodyDarkClass";
import { dteDocumentsApi } from "api/endpoints";
import { useShallow } from "zustand/react/shallow";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";
import { DTE_TIPO_FILTER_SELECT_OPTIONS } from "utils/cafXmlPreview";

const NET_SALES_INCLUDED_TIPOS = new Set([33, 39, 41]);
const NET_SALES_DISCOUNT_TIPOS = new Set([61]);
const SALES_ORDER_TIPOS = new Set([33, 39, 41]);

function isoTodayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/** Inicio del rango “últimos N días” incluyendo hoy (N=7 → desde hace 6 días). */
function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function parseYMD(iso) {
  const [y, m, day] = String(iso).split("-").map(Number);
  return new Date(y, m - 1, day);
}

function formatYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function eachDayInclusive(desde, hasta) {
  const out = [];
  const a = parseYMD(desde);
  const b = parseYMD(hasta);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || a > b) return out;
  const cur = new Date(a);
  while (cur <= b) {
    out.push(formatYMD(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function daysInMonthFromIso(iso) {
  const d = parseYMD(iso);
  if (Number.isNaN(d.getTime())) return 0;
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

function resolveDetalleItems(item) {
  if (!item || typeof item !== "object") return [];
  const direct = item.detalle_items;
  if (Array.isArray(direct) && direct.length > 0) return direct;

  const pn = item.payload_normalized;
  if (pn && typeof pn === "object") {
    const candidates = [
      pn.detalle,
      pn.detalle_items,
      pn.items,
      pn.factura?.detalle,
      pn.boleta?.detalle,
      pn.notaCredito?.detalle,
      pn.notaDebito?.detalle,
      pn.guiaDespacho?.detalle,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) return candidate;
    }
  }

  const po = item.payload_original;
  if (po && typeof po === "object") {
    const candidates = [
      po.items,
      po.detalle,
      po.detalle_items,
      po.factura?.detalle,
      po.invoice?.items,
      po.invoice?.detalle,
      po.boleta?.detalle,
      po.notaCredito?.detalle,
      po.notaDebito?.detalle,
      po.guiaDespacho?.detalle,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) return candidate;
    }
  }

  return [];
}

function extractLineQty(line) {
  const raw = line?.QtyItem ?? line?.qtyItem ?? line?.cantidad ?? line?.qty;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Alinea fechas del API con categorías YYYY-MM-DD. */
function normalizeApiDateKey(val) {
  if (val == null || val === "") return "";
  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    return formatYMD(val);
  }
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return formatYMD(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatCLP(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(x);
}

function compactNumber(val) {
  return new Intl.NumberFormat("es-CL", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(val);
}

/** Colores legibles en modo oscuro (Apex no usa Tailwind `dark:`). */
function mergeApexTheme(opts, isDark) {
  if (!isDark) {
    return { ...opts, theme: { mode: "light" } };
  }
  const fg = "#e2e8f0";
  const muted = "#94a3b8";
  const gridBorder = "rgba(148,163,184,0.22)";
  return {
    ...opts,
    theme: { mode: "dark" },
    chart: {
      ...opts.chart,
      foreColor: fg,
      background: "transparent",
    },
    grid: {
      ...opts.grid,
      borderColor: gridBorder,
    },
    legend:
      opts.legend && opts.legend.show === false
        ? opts.legend
        : {
            ...opts.legend,
            labels: { ...opts.legend?.labels, colors: fg },
          },
    xaxis: {
      ...opts.xaxis,
      labels: {
        ...opts.xaxis.labels,
        style: {
          ...opts.xaxis.labels?.style,
          colors: muted,
        },
      },
    },
    yaxis: {
      ...opts.yaxis,
      title: {
        ...opts.yaxis.title,
        style: { color: muted, ...opts.yaxis.title?.style },
      },
      labels: {
        ...opts.yaxis.labels,
        style: {
          ...opts.yaxis.labels?.style,
          colors: muted,
        },
      },
    },
    tooltip: {
      ...opts.tooltip,
      theme: "dark",
    },
  };
}

export default function DteEmissionStatsChart({
  desde: desdeProp,
  hasta: hastaProp,
  onDesdeChange,
  onHastaChange,
  hideControls = false,
  onSummaryChange,
}) {
  const isDark = useBodyDarkClass();
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
      rutEmisor: s.rutEmisor,
    })),
  );
  const selectedAmbiente =
    emission.kind === "ready" && emission.ambiente ? emission.ambiente : "";
  const selectedRutEmisor =
    emission.kind === "ready" && emission.rutEmisor ? String(emission.rutEmisor).trim() : "";
  const [localDesde, setLocalDesde] = React.useState(() => isoDaysAgo(6));
  const [localHasta, setLocalHasta] = React.useState(() => isoTodayLocal());
  const desde = desdeProp ?? localDesde;
  const hasta = hastaProp ?? localHasta;
  const setDesde = onDesdeChange ?? setLocalDesde;
  const setHasta = onHastaChange ?? setLocalHasta;
  const [tipoDte, setTipoDte] = React.useState("");
  const [state, setState] = React.useState({
    loading: true,
    error: null,
    items: [],
    netItems: [],
    orderItems: [],
  });

  React.useEffect(() => {
    if (emission.kind !== "ready" || !selectedAmbiente) {
      setState({ loading: false, error: null, items: [], netItems: [], orderItems: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const buildStatsQs = (selectedTipoDte = "") => {
          const qs = new URLSearchParams();
          qs.set("fch_emis_desde", desde);
          qs.set("fch_emis_hasta", hasta);
          qs.set("lifecycle_status", "accepted");
          if (selectedAmbiente) qs.set("modo", selectedAmbiente);
          if (selectedRutEmisor) qs.set("rut_emisor", selectedRutEmisor);
          if (selectedTipoDte) qs.set("tipo_dte", selectedTipoDte);
          return qs.toString();
        };
        const [res, netRes, ordersRes] = await Promise.all([
          dteDocumentsApi.emissionStats(`?${buildStatsQs(tipoDte)}`),
          dteDocumentsApi.emissionStats(`?${buildStatsQs("")}`),
          dteDocumentsApi.list(
            `?${new URLSearchParams({
              fch_emis_desde: desde,
              fch_emis_hasta: hasta,
              lifecycle_status: "accepted",
              ...(selectedAmbiente ? { modo: selectedAmbiente } : {}),
              ...(selectedRutEmisor ? { rut_emisor: selectedRutEmisor } : {}),
              limit: "500",
              page: "1",
              sort_by: "fch_emis",
              sort_dir: "desc",
            }).toString()}`,
          ),
        ]);
        if (cancelled) return;
        if (
          !res?.res?.ok ||
          res?.data?.ok === false ||
          !netRes?.res?.ok ||
          netRes?.data?.ok === false ||
          !ordersRes?.res?.ok ||
          ordersRes?.data?.ok === false
        ) {
          setState({
            loading: false,
            error:
              res?.data?.error ||
              res?.data?.message ||
              netRes?.data?.error ||
              netRes?.data?.message ||
              ordersRes?.data?.error ||
              ordersRes?.data?.message ||
              "No se pudieron cargar las estadísticas.",
            items: [],
            netItems: [],
            orderItems: [],
          });
          return;
        }
        setState({
          loading: false,
          error: null,
          items: res.data?.items || [],
          netItems: netRes.data?.items || [],
          orderItems: ordersRes.data?.items || [],
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          loading: false,
          error: e instanceof Error ? e.message : "Error de red.",
          items: [],
          netItems: [],
          orderItems: [],
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [desde, hasta, tipoDte, selectedAmbiente, selectedRutEmisor, emission.kind]);

  const charts = React.useMemo(() => {
    const categories = eachDayInclusive(desde, hasta);
    if (categories.length === 0) {
      return { invalidRange: true };
    }

    const netItems = state.netItems;
    const orderItems = Array.isArray(state.orderItems) ? state.orderItems : [];

    const netSalesByDate = new Map();
    let facturaAmount = 0;
    let facturaCount = 0;
    let boletaAmount = 0;
    let boletaCount = 0;
    let creditNoteAmount = 0;
    let creditNoteCount = 0;
    for (const row of netItems) {
      const tipo = Number(row.tipo_dte || 0);
      if (!NET_SALES_INCLUDED_TIPOS.has(tipo) && !NET_SALES_DISCOUNT_TIPOS.has(tipo)) {
        continue;
      }
      const key = normalizeApiDateKey(row.emis_date);
      if (!key) continue;
      const amount = Number(row.monto_sum || 0);
      const count = Number(row.document_count || 0);
      if (tipo === 33) {
        facturaAmount += amount;
        facturaCount += count;
      } else if (tipo === 39 || tipo === 41) {
        boletaAmount += amount;
        boletaCount += count;
      } else if (tipo === 61) {
        creditNoteAmount += amount;
        creditNoteCount += count;
      }
      const signedAmount = NET_SALES_DISCOUNT_TIPOS.has(tipo) ? -amount : amount;
      netSalesByDate.set(key, (netSalesByDate.get(key) || 0) + signedAmount);
    }
    const netSalesData = categories.map((d) => netSalesByDate.get(d) || 0);
    const netSalesTotal = netSalesData.reduce((acc, value) => acc + Number(value || 0), 0);
    const grossSalesTotal = facturaAmount + boletaAmount;
    const salesCount = facturaCount + boletaCount;
    const avgTicket = salesCount > 0 ? netSalesTotal / salesCount : 0;
    const avgFactura = facturaCount > 0 ? facturaAmount / facturaCount : 0;
    const avgBoleta = boletaCount > 0 ? boletaAmount / boletaCount : 0;
    const observedDays = categories.length;
    const avgDailyNetSales = observedDays > 0 ? netSalesTotal / observedDays : 0;
    const projectionDays = daysInMonthFromIso(hasta);
    const projectedMonthNetSales =
      projectionDays > 0 ? avgDailyNetSales * projectionDays : netSalesTotal;
    const acceptedOrders = orderItems.filter((row) => SALES_ORDER_TIPOS.has(Number(row?.tipo_dte || 0)));
    const totalUnits = acceptedOrders.reduce(
      (sum, row) =>
        sum +
        resolveDetalleItems(row).reduce(
          (lineSum, line) => lineSum + extractLineQty(line),
          0,
        ),
      0,
    );
    const unitsPerOrder = acceptedOrders.length > 0 ? totalUnits / acceptedOrders.length : 0;

    const montoSeries = [{ name: "Ventas netas (CLP)", data: netSalesData }];

    const commonXaxis = {
      categories,
      labels: {
        rotate: -35,
        rotateAlways: categories.length > 10,
        style: { fontSize: "10px" },
      },
    };

    const montoOptions = {
      chart: {
        type: "line",
        fontFamily: "inherit",
        toolbar: { show: true },
        zoom: { enabled: true },
      },
      stroke: { width: 3, curve: "smooth" },
      colors: ["#16a34a"],
      markers: { size: 0, hover: { size: 4 } },
      dataLabels: { enabled: false },
      xaxis: commonXaxis,
      yaxis: {
        title: { text: "Monto (CLP)" },
        labels: {
          style: { fontSize: "11px" },
          formatter: (val) => compactNumber(val),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) => formatCLP(val),
        },
      },
      legend: { show: false },
      grid: {
        borderColor: "rgba(148,163,184,0.35)",
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
      },
    };

    return {
      invalidRange: false,
      montoSeries,
      montoOptions: mergeApexTheme(montoOptions, isDark),
      netSalesTotal,
      grossSalesTotal,
      creditNoteAmount,
      salesCount,
      facturaCount,
      boletaCount,
      creditNoteCount,
      avgTicket,
      avgFactura,
      avgBoleta,
      avgDailyNetSales,
      projectedMonthNetSales,
      totalUnits,
      unitsPerOrder,
    };
  }, [state.netItems, state.orderItems, desde, hasta, tipoDte, isDark]);

  React.useEffect(() => {
    if (typeof onSummaryChange !== "function") return;
    if (state.loading || state.error || charts.invalidRange) {
      onSummaryChange(null);
      return;
    }
    onSummaryChange({
      netSalesTotal: charts.netSalesTotal,
      grossSalesTotal: charts.grossSalesTotal,
      creditNoteAmount: charts.creditNoteAmount,
      salesCount: charts.salesCount,
      facturaCount: charts.facturaCount,
      boletaCount: charts.boletaCount,
      creditNoteCount: charts.creditNoteCount,
      avgTicket: charts.avgTicket,
      avgFactura: charts.avgFactura,
      avgBoleta: charts.avgBoleta,
      avgDailyNetSales: charts.avgDailyNetSales,
      projectedMonthNetSales: charts.projectedMonthNetSales,
      totalUnits: charts.totalUnits,
      unitsPerOrder: charts.unitsPerOrder,
    });
  }, [charts, onSummaryChange, state.loading, state.error]);

  const chartKey = `${desde}|${hasta}|${tipoDte}|${state.items.length}|${isDark ? "d" : "l"}`;

  return (
    <Card extra="mt-5 rounded-[20px] p-3 sm:p-4">
      <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h3 className="text-[11px] font-bold uppercase tracking-wide text-navy-700 dark:text-white">
            Emisión DTE — foco comercial
          </h3>
          <p className="mt-0.5 text-[9px] text-gray-500 dark:text-gray-400 sm:text-[10px]">
            Solo considera documentos <span className="font-medium">accepted</span>.
            Este bloque se concentra en ventas netas y su proyección para análisis comercial.
          </p>
        </div>
        {!hideControls ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
              Desde
              <input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-navy-700 dark:border-white/15 dark:bg-navy-800 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
              Hasta
              <input
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-navy-700 dark:border-white/15 dark:bg-navy-800 dark:text-white"
              />
            </label>
            <label className="flex min-w-[10rem] flex-col gap-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
              Tipo DTE
              <select
                value={tipoDte}
                onChange={(e) => setTipoDte(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-navy-700 dark:border-white/15 dark:bg-navy-800 dark:text-white"
              >
                <option value="">Todos los tipos</option>
                {DTE_TIPO_FILTER_SELECT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          <label className="flex min-w-[10rem] flex-col gap-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
            Tipo DTE
            <select
              value={tipoDte}
              onChange={(e) => setTipoDte(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-navy-700 dark:border-white/15 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Todos los tipos</option>
              {DTE_TIPO_FILTER_SELECT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {state.loading ? (
        <p className="py-8 text-center text-xs text-gray-500 dark:text-gray-400">
          Cargando serie…
        </p>
      ) : state.error ? (
        <p className="py-6 text-center text-xs text-red-600 dark:text-red-400">
          {state.error}
        </p>
      ) : charts.invalidRange ? (
        <p className="py-6 text-center text-xs text-amber-700 dark:text-amber-300">
          El rango de fechas no es válido: &quot;Desde&quot; debe ser anterior o
          igual a &quot;Hasta&quot;.
        </p>
      ) : (
        <div className="min-w-0 w-full">
          <div className="mb-1 flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Ventas netas por día
              </p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                Total rango:{" "}
                <span className="font-bold text-emerald-700 dark:text-emerald-300">
                  {formatCLP(charts.netSalesTotal)}
                </span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Proyección mes
              </p>
              <span className="text-[11px] font-bold text-brand-500 dark:text-brand-300">
                {formatCLP(charts.projectedMonthNetSales)}
              </span>
            </div>
          </div>
          <p className="mb-2 text-[10px] text-gray-500 dark:text-gray-400">
            Fórmula: (Facturas 33 + Boletas 39/41) - Notas de crédito 61.
            Solo incluye DTE aceptados por SII.
          </p>
          <p className="mb-2 text-[10px] text-gray-500 dark:text-gray-400">
            Estimación lineal usando promedio diario del rango:{" "}
            <span className="font-medium">{formatCLP(charts.avgDailyNetSales)}</span>.
          </p>
          <div className="overflow-hidden rounded-lg border border-gray-100 bg-white/60 dark:border-white/10 dark:bg-navy-900/40">
            <Chart
              key={`${chartKey}-monto`}
              type="line"
              options={charts.montoOptions}
              series={charts.montoSeries}
              width="100%"
              height={260}
            />
          </div>
        </div>
      )}
    </Card>
  );
}
